import { create } from "zustand";
import { shallow } from "zustand/shallow";
import type {
  SupportMessageProductContext,
  SupportUserConversationMessageAttachment,
} from "../admin-pages/user-admin-pages/admin_support/threads_support/shared/supportConversationsTypes";

/**
 * UI state for the floating customer support chat panel.
 *
 * Kept intentionally small: the *data* (conversations + messages) lives in
 * Firestore listeners; this store only tracks navigation, the streaming bot
 * draft, and a minimal flag for whether the user has the panel open.
 */

export type CustomerSupportChatPanelView = "feedback_options" | "conversations_list" | "active_conversation";

export type CustomerSupportNewConversationEntryRequest = {
  requestId: number;
  feedbackTypeTag: "Bug";
  initialMessageContent: string | null;
  shouldAutoSubmit: boolean;
};

export type CustomerSupportOutgoingUserMessage = {
  clientMessageId: string;
  conversationId: string;
  content: string;
  attachments: SupportUserConversationMessageAttachment[];
  /** Send-time review evidence reused verbatim if this message is retried. */
  productContext: SupportMessageProductContext | null;
  createdAt: string;
  /** Exact reply id reserved by the Worker's `ready` event, when observed. */
  expectedBotMessageId: string | null;
};

interface CustomerSupportChatStoreState {
  isPanelOpen: boolean;
  /**
   * Evidence capture hides support without closing the logical panel. While
   * hidden, the product must regain its keyboard shortcuts so the customer
   * can demonstrate the reported behavior in a recording or screenshot.
   */
  isPanelTemporarilyHiddenForEvidenceCapture: boolean;
  currentView: CustomerSupportChatPanelView;
  activeConversationId: string | null;
  pendingNewConversationEntryRequest: CustomerSupportNewConversationEntryRequest | null;

  /**
   * The bot's response is streamed token by token. The currently-streaming
   * draft is held in this store so the active conversation view can render
   * it without re-rendering the whole transcript on each token.
   */
  streamingBotMessageDraftContent: string;
  isBotStreamingResponse: boolean;
  hasStreamingErrorOccurred: boolean;
  streamingConversationId: string | null;
  streamingBotPersistedMessageId: string | null;
  pendingOutgoingUserMessage: CustomerSupportOutgoingUserMessage | null;
  /**
   * Failed sends must outlive Firestore's optimistic-message handoff and
   * navigation to another thread. Keying by conversation prevents a send in
   * thread B from destroying thread A's retry payload.
   */
  failedOutgoingUserMessagesByConversationId: Record<string, CustomerSupportOutgoingUserMessage>;
  pendingInterimBotMessage: {
    messageId: string;
    conversationId: string;
    content: string;
    createdAt: string;
  } | null;
  /** A separately persisted same-turn bubble rendered after the main reply. */
  pendingFollowUpBotMessage: {
    messageId: string;
    conversationId: string;
    content: string;
    createdAt: string;
  } | null;

  /**
   * Identifies the turn that owns the draft above.
   *
   * Every mutation carries the id of the turn it came from, so a late event
   * from a stream that has already been aborted or superseded (the user sent
   * again, switched conversation, closed the panel) lands on nothing instead
   * of rewriting the reply now on screen.
   */
  streamingBotTurnId: string | null;
  /** Placeholder id the worker's `ready` event gave this turn's reply, if any. */
  streamingBotMessagePlaceholderId: string | null;
  /** Reserved id for a same-turn companion bubble, if the turn emits one. */
  streamingBotFollowUpMessagePlaceholderId: string | null;
  /**
   * True once `reply_corrected` replaced this turn's draft.
   *
   * The corrected text is the whole final reply and the worker emits it as the
   * last content event of the turn, so nothing may append to it afterwards —
   * that is what keeps a duplicated or re-ordered frame from putting the false
   * sentence back on screen.
   */
  hasCorrectedStreamingBotMessageDraft: boolean;

  openPanel: () => void;
  closePanel: () => void;
  setIsPanelTemporarilyHiddenForEvidenceCapture: (isHidden: boolean) => void;
  setCurrentView: (view: CustomerSupportChatPanelView) => void;
  setActiveConversationId: (conversationId: string | null) => void;
  requestNewBugConversation: (args?: {
    initialMessageContent?: string | null;
    shouldAutoSubmit?: boolean;
  }) => number;
  clearPendingNewConversationEntryRequest: (requestId: number) => void;

  /** Starts a turn and returns its id; pass that id back on every event. */
  startBotStreamingDraft: (conversationId: string) => string;
  setPendingOutgoingUserMessage: (pendingMessage: CustomerSupportChatStoreState["pendingOutgoingUserMessage"]) => void;
  clearPendingOutgoingUserMessage: (clientMessageId: string) => void;
  recordFailedOutgoingUserMessage: (failedMessage: CustomerSupportOutgoingUserMessage) => void;
  clearFailedOutgoingUserMessage: (conversationId: string, clientMessageId: string) => void;
  setPendingInterimBotMessage: (message: NonNullable<CustomerSupportChatStoreState["pendingInterimBotMessage"]>) => void;
  clearPendingInterimBotMessage: (messageId: string) => void;
  setPendingFollowUpBotMessage: (message: NonNullable<CustomerSupportChatStoreState["pendingFollowUpBotMessage"]>) => void;
  clearPendingFollowUpBotMessage: (messageId: string) => void;
  setStreamingBotMessagePlaceholderId: (args: { turnId: string; placeholderId: string }) => void;
  setStreamingBotFollowUpMessagePlaceholderId: (args: { turnId: string; placeholderId: string }) => void;
  appendBotStreamingDraftContent: (deltaContent: string, turnId?: string | null) => void;
  /**
   * Replace the streamed draft with the reply as it was actually persisted.
   *
   * Replace, never append: the point of the correction is that the customer
   * stops reading the false sentence, and a retraction underneath it leaves
   * the claim on screen. Ignored unless this turn is still the one streaming
   * and the correction names this turn's reply (or names nothing at all).
   */
  applyBotStreamingDraftCorrection: (args: {
    turnId: string;
    correctedText: string;
    targetBotMessageId: string | null;
  }) => void;
  /**
   * Terminal actions accept the acting turn's id so a stale turn that errors
   * or closes late can never wipe the state of a newer live turn. Callers
   * without a turn (the conversation view's cleanup) may omit it.
   */
  finishBotStreamingDraft: (turnId?: string) => void;
  markBotStreamingDraftPersisted: (args: { turnId: string; botMessageId: string }) => void;
  recordBotStreamingError: (turnId?: string) => void;
}

let lastIssuedBotStreamingTurnNumber = 0;
let lastIssuedNewConversationEntryRequestNumber = 0;

export const useCustomerSupportChatStore = create<CustomerSupportChatStoreState>((set) => ({
  isPanelOpen: false,
  isPanelTemporarilyHiddenForEvidenceCapture: false,
  currentView: "feedback_options",
  activeConversationId: null,
  pendingNewConversationEntryRequest: null,
  streamingBotMessageDraftContent: "",
  isBotStreamingResponse: false,
  hasStreamingErrorOccurred: false,
  streamingConversationId: null,
  streamingBotPersistedMessageId: null,
  pendingOutgoingUserMessage: null,
  failedOutgoingUserMessagesByConversationId: {},
  pendingInterimBotMessage: null,
  pendingFollowUpBotMessage: null,
  streamingBotTurnId: null,
  streamingBotMessagePlaceholderId: null,
  streamingBotFollowUpMessagePlaceholderId: null,
  hasCorrectedStreamingBotMessageDraft: false,

  openPanel: () => set({ isPanelOpen: true, isPanelTemporarilyHiddenForEvidenceCapture: false }),
  closePanel: () => set({ isPanelOpen: false, isPanelTemporarilyHiddenForEvidenceCapture: false }),
  setIsPanelTemporarilyHiddenForEvidenceCapture: (isPanelTemporarilyHiddenForEvidenceCapture) =>
    set({ isPanelTemporarilyHiddenForEvidenceCapture }),
  setCurrentView: (view) => set({ currentView: view }),
  setActiveConversationId: (conversationId) => set({ activeConversationId: conversationId }),
  requestNewBugConversation: ({ initialMessageContent = null, shouldAutoSubmit } = {}) => {
    lastIssuedNewConversationEntryRequestNumber += 1;
    const requestId = lastIssuedNewConversationEntryRequestNumber;
    const trimmedInitialMessageContent = initialMessageContent?.trim() || null;
    set({
      isPanelOpen: true,
      isPanelTemporarilyHiddenForEvidenceCapture: false,
      currentView: "feedback_options",
      activeConversationId: null,
      pendingNewConversationEntryRequest: {
        requestId,
        feedbackTypeTag: "Bug",
        initialMessageContent: trimmedInitialMessageContent,
        shouldAutoSubmit: shouldAutoSubmit ?? trimmedInitialMessageContent !== null,
      },
    });
    return requestId;
  },
  clearPendingNewConversationEntryRequest: (requestId) =>
    set((state) =>
      state.pendingNewConversationEntryRequest?.requestId === requestId
        ? { pendingNewConversationEntryRequest: null }
        : {}
    ),

  startBotStreamingDraft: (conversationId) => {
    lastIssuedBotStreamingTurnNumber += 1;
    const turnId = `support_bot_turn_${lastIssuedBotStreamingTurnNumber}`;
    set({
      streamingBotMessageDraftContent: "",
      isBotStreamingResponse: true,
      hasStreamingErrorOccurred: false,
      streamingConversationId: conversationId,
      streamingBotPersistedMessageId: null,
      pendingFollowUpBotMessage: null,
      streamingBotTurnId: turnId,
      streamingBotMessagePlaceholderId: null,
      streamingBotFollowUpMessagePlaceholderId: null,
      hasCorrectedStreamingBotMessageDraft: false,
    });
    return turnId;
  },

  setPendingOutgoingUserMessage: (pendingOutgoingUserMessage) => set({ pendingOutgoingUserMessage }),

  clearPendingOutgoingUserMessage: (clientMessageId) =>
    set((state) =>
      state.pendingOutgoingUserMessage?.clientMessageId === clientMessageId ? { pendingOutgoingUserMessage: null } : {}
    ),

  recordFailedOutgoingUserMessage: (failedMessage) =>
    set((state) => ({
      failedOutgoingUserMessagesByConversationId: {
        ...state.failedOutgoingUserMessagesByConversationId,
        [failedMessage.conversationId]: failedMessage,
      },
    })),

  clearFailedOutgoingUserMessage: (conversationId, clientMessageId) =>
    set((state) => {
      const failedMessage = state.failedOutgoingUserMessagesByConversationId[conversationId];
      if (!failedMessage || failedMessage.clientMessageId !== clientMessageId) return {};
      const { [conversationId]: _clearedFailedMessage, ...remainingFailedMessages } =
        state.failedOutgoingUserMessagesByConversationId;
      return { failedOutgoingUserMessagesByConversationId: remainingFailedMessages };
    }),

  setPendingInterimBotMessage: (pendingInterimBotMessage) =>
    set({ pendingInterimBotMessage }),

  clearPendingInterimBotMessage: (messageId) =>
    set((state) =>
      state.pendingInterimBotMessage?.messageId === messageId
        ? { pendingInterimBotMessage: null }
        : {}
    ),

  setPendingFollowUpBotMessage: (pendingFollowUpBotMessage) =>
    set({ pendingFollowUpBotMessage }),

  clearPendingFollowUpBotMessage: (messageId) =>
    set((state) =>
      state.pendingFollowUpBotMessage?.messageId === messageId
        ? { pendingFollowUpBotMessage: null }
        : {}
    ),

  setStreamingBotMessagePlaceholderId: ({ turnId, placeholderId }) =>
    set((state) =>
      state.isBotStreamingResponse && state.streamingBotTurnId === turnId
        ? { streamingBotMessagePlaceholderId: placeholderId }
        : {}
    ),

  setStreamingBotFollowUpMessagePlaceholderId: ({ turnId, placeholderId }) =>
    set((state) =>
      state.isBotStreamingResponse && state.streamingBotTurnId === turnId
        ? { streamingBotFollowUpMessagePlaceholderId: placeholderId }
        : {}
    ),

  appendBotStreamingDraftContent: (deltaContent, turnId) =>
    set((state) => {
      if (typeof deltaContent !== "string" || deltaContent.length === 0) return {};
      if (turnId != null && state.streamingBotTurnId !== turnId) return {};
      // Once the turn has been corrected its text is final. Appending here
      // would grow a reply the worker has already replaced.
      if (state.hasCorrectedStreamingBotMessageDraft) return {};
      return {
        streamingBotMessageDraftContent: state.streamingBotMessageDraftContent + deltaContent,
      };
    }),

  applyBotStreamingDraftCorrection: ({ turnId, correctedText, targetBotMessageId }) =>
    set((state) => {
      // Nothing is on screen to correct: the turn ended (the persisted message
      // already carries the corrected text) or was aborted. Writing the draft
      // back here would resurrect a bubble the panel has finished with.
      if (!state.isBotStreamingResponse) return {};
      if (state.streamingBotTurnId !== turnId) return {};
      // A correction that names a reply other than the one rendering belongs to
      // some other turn; applying it would falsify an unrelated message.
      if (
        targetBotMessageId !== null &&
        state.streamingBotMessagePlaceholderId !== null &&
        state.streamingBotMessagePlaceholderId !== targetBotMessageId
      ) {
        return {};
      }
      // Idempotent by construction: assignment, not concatenation, so a
      // duplicate event re-applies the same text and changes nothing.
      return {
        streamingBotMessageDraftContent: correctedText,
        hasCorrectedStreamingBotMessageDraft: true,
      };
    }),

  finishBotStreamingDraft: (turnId) =>
    set((state) => {
      if (turnId !== undefined && state.streamingBotTurnId !== turnId) return {};
      return {
        streamingBotMessageDraftContent: "",
        isBotStreamingResponse: false,
        streamingConversationId: null,
        streamingBotPersistedMessageId: null,
        streamingBotTurnId: null,
        streamingBotMessagePlaceholderId: null,
        streamingBotFollowUpMessagePlaceholderId: null,
        hasCorrectedStreamingBotMessageDraft: false,
      };
    }),

  markBotStreamingDraftPersisted: ({ turnId, botMessageId }) =>
    set((state) =>
      state.streamingBotTurnId === turnId
        ? {
            streamingBotPersistedMessageId: botMessageId,
          }
        : {}
    ),

  recordBotStreamingError: (turnId) =>
    set((state) => {
      if (turnId !== undefined && state.streamingBotTurnId !== turnId) return {};
      return {
        isBotStreamingResponse: false,
        hasStreamingErrorOccurred: true,
        streamingConversationId: null,
        streamingBotPersistedMessageId: null,
        streamingBotTurnId: null,
        streamingBotMessagePlaceholderId: null,
        streamingBotFollowUpMessagePlaceholderId: null,
        hasCorrectedStreamingBotMessageDraft: false,
      };
    }),
}));

// ---------------------------------------------------------------------------
// Selectors — keep components subscribed to the smallest viable slice.
// ---------------------------------------------------------------------------

export const useCustomerSupportChatPanelOnly = () =>
  useCustomerSupportChatStore(
    (state) => ({
      isPanelOpen: state.isPanelOpen,
      currentView: state.currentView,
      activeConversationId: state.activeConversationId,
    }),
    shallow
  );

export const useCustomerSupportChatStreamingOnly = () =>
  useCustomerSupportChatStore(
    (state) => ({
      streamingBotMessageDraftContent: state.streamingBotMessageDraftContent,
      isBotStreamingResponse: state.isBotStreamingResponse,
      hasStreamingErrorOccurred: state.hasStreamingErrorOccurred,
      streamingConversationId: state.streamingConversationId,
      streamingBotPersistedMessageId: state.streamingBotPersistedMessageId,
      streamingBotMessagePlaceholderId: state.streamingBotMessagePlaceholderId,
      streamingBotFollowUpMessagePlaceholderId: state.streamingBotFollowUpMessagePlaceholderId,
      pendingOutgoingUserMessage: state.pendingOutgoingUserMessage,
      failedOutgoingUserMessagesByConversationId: state.failedOutgoingUserMessagesByConversationId,
      pendingInterimBotMessage: state.pendingInterimBotMessage,
      pendingFollowUpBotMessage: state.pendingFollowUpBotMessage,
    }),
    shallow
  );
